import React from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';

import PrivateRoute from 'app/components/auth/PrivateRoute';
import PublicRoute from 'app/components/auth/PublicRoute';

import OnBoardingLayout from 'app/features/on-boarding/OnBoardingLayout';
import AuthenticationLayout from 'app/features/authentication/AuthenticationLayout';
import AuthenticationConnectLayout from 'app/features/authentication-connect/AuthenticationConnectLayout';
import DashboardLayout from 'app/features/dashboard/DashboardLayout';
import EventsLayout from 'app/features/events/EventsLayout';
import CustomersLayout from 'app/features/customers/CustomersLayout';
import PaymentsLayout from 'app/features/payments/PaymentsLayout';
import ImportsLayout from 'app/features/imports/ImportsLayout';
import TeamsLayout from 'app/features/teams/TeamsLayout';
import SettingsView from 'app/features/settings/components/SettingsView';
import NotFoundView from 'app/components/layout/NotFound';
import CompetitionsLayout from 'app/features/competitions/CompetitionsLayout';
import OrganisationFeesLayout from 'app/features/organisation-fees/OrganisationFeesLayout';
import NotificationConfigsLayout from 'app/features/notification-configs/NotificationConfigsLayout';
import ConnectionsLayout from 'app/features/connections/ConnectionsLayout';
import ConnectionRunsLayout from 'app/features/connection-runs/ConnectionRunsLayout';
import ReportsLayout from 'app/features/reports/ReportsLayout';
import WelcomeLayout from 'app/features/welcome/WelcomeLayout';
import BillingLayout from 'app/features/billing/BillingLayout';
import MaintenanceView from 'app/components/layout/Maintenance';

import { IAdminsSelector, IOrganisationsSelector } from 'app/types/selector';

interface IRouteProps {
  flags: any;
  ldClient: any;
  admins: IAdminsSelector;
  organisations: IOrganisationsSelector;
  authentication: any;
  actions: any;
  location: any;
  match: any;
}

export default class Routes extends React.Component<IRouteProps, {}> {
  previousLocation;

  previousPath;

  static defaultProps: IRouteProps = {
    flags: undefined,
    ldClient: undefined,
    admins: undefined,
    authentication: undefined,
    organisations: undefined,
    actions: undefined,
    location: undefined,
    match: undefined,
  };

  constructor(props) {
    super(props);
    this.previousLocation = this.props.location;
    this.previousPath = this.props.location.state?.previousPath;
  }

  componentWillUpdate(): void {
    const { location } = this.props;

    if (!(location.state && location.state.modal)) {
      this.previousLocation = location;
    }
    const previousPath = location.state?.previousPath;
    if (previousPath !== undefined) {
      this.previousPath = previousPath;
    }
  }


  render(): React.ReactNode {
    const featureFlags = this.props.organisations?.currentOrganisation?.featureFlags;
    const teammoAdmin = this.props.admins?.currentAdmin?.roleType === 'teammo-admin';
    const bolShowDashboard = teammoAdmin || featureFlags?.showAnalyticsDashboard || true;
    const bolShowPaymentRequests = teammoAdmin || featureFlags?.showPaymentRequests || false;
    const bolShowMatchPayments = teammoAdmin || featureFlags?.showMatchPayments || false;
    const bolShowCustomers = teammoAdmin || featureFlags?.showCustomers || false;
    const bolShowTeams = teammoAdmin || featureFlags?.showTeams || false;
    const bolShowEventDashboard = teammoAdmin || featureFlags?.showEventDashboard || true;
    const bolShowConnections = teammoAdmin || featureFlags?.showConnections || true;
    const bolShowReports = teammoAdmin || featureFlags?.showReports || true;
    const bolShowBilling = teammoAdmin || featureFlags?.showBilling || false;

    const { location } = this.props;
    const isModal = (
      location.state
      && location.state.modal
      && this.previousLocation !== location
    );

    const { previousPath } = this;
    return (
      <>
        <Switch location={isModal ? this.previousLocation : location}>
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/', '/dashboard']}
            component={DashboardLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/']}
            component={CustomersLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/']}
            component={CompetitionsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/events', '/events/import']}
            component={EventsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={[
              '/competitions',
              '/competitions/:competitionId',
              '/create-competition',
            ]}
            component={CompetitionsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={[
              '/reports',
              '/reports/:reportType',
            ]}
            component={ReportsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={[
              '/billing',
              '/billing/select-plan',
            ]}
            component={BillingLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={[
              '/import/customers',
              '/import/customers/map',
              '/import/customers/review/:connectionRunId',
              '/import/teams',
              '/import/teams/map',
              '/import/teams/review/:connectionRunId',
              '/import/match-payments',
              '/import/match-payments/map',
              '/import/match-payments/review/:connectionRunId',
            ]}
            component={ImportsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={[
              '/connections',
              '/connections/:connectionId',
              '/connections/:connectionId/runs',
              '/create-connection',
              '/create-connection/:connectionType',
            ]}
            component={ConnectionsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={[
              '/welcome',
              '/welcome/:provider',
            ]}
            component={WelcomeLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            path={[
              '/connections/:connectionId/run',
              '/connections/:connectionId/run/csv',
              '/connections/:connectionId/run/csv/fixture',
              '/connections/:connectionId/run/csv/fixture/map',
              '/connections/:connectionId/run/csv/team-contact',
              '/connections/:connectionId/run/csv/team-contact/map',
              '/connections/:connectionId/runs/:connectionRunId',
            ]}
            component={ConnectionRunsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/fees/:organisationFeeId', '/create-fee']}
            component={OrganisationFeesLayout}
          />

          <PrivateRoute
            actions={this.props.actions}
            exact
            path={[
              '/notification-contacts/:notificationConfigId',
              '/create-notification-contact',
            ]}
            component={NotificationConfigsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            path="/events/imports/:importId"
            component={EventsLayout}
          />
          <PublicRoute
            exact
            path={[
              '/login',
              '/login/suudo',
              '/forgot-password',
              '/forgot-password/confirm',
              '/login/connect/:provider',
            ]}
            component={AuthenticationLayout}
          />
          <PublicRoute
            exact
            path={[
              '/connect/:provider',
              '/connect/:provider/:prefilled',
            ]}
            component={AuthenticationConnectLayout}
          />

          <PublicRoute exact path="/404" component={NotFoundView} />
          <PublicRoute exact path="/maintenance" component={MaintenanceView} />
          <PublicRoute
            exact
            path={[
              '/sign-up',
              '/get-started',
              '/get-started/personal',
              '/get-started/verify',
              '/get-started/connect/:provider',
              '/get-started/personal/connect/:provider',
              '/get-started/verify/connect/:provider',
              '/get-started/account',
            ]}
            component={OnBoardingLayout}
          />

          <PublicRoute
            path="/get-started/invite/:inviteToken"
            component={OnBoardingLayout}
          />

          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/customers', '/customers\\::filterConfig']}
            component={CustomersLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            path="/customers/detail/:customerId"
            component={CustomersLayout}
          />

          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/payments']}
            component={PaymentsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/payments/segments/matches', '/payments/segments/matches\\::filterConfig', '/payments/segments/matches/:action', '/payments/segments/matches/:action/:subaction']}
            component={PaymentsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/payments/segments/requests', '/payments/segments/requests\\::filterConfig', '/payments/segments/requests/:action', '/payments/segments/requests/:action/:subaction', '/payments/segments/requests/detail/:action/collection/:subaction']}
            component={PaymentsLayout}
          />

          <PrivateRoute
            actions={this.props.actions}
            exact
            path="/teams/detail/:teamId"
            component={TeamsLayout}
          />
          <PrivateRoute
            actions={this.props.actions}
            exact
            path={['/teams', '/teams\\::filterConfig']}
            component={TeamsLayout}
          />

          <PublicRoute
            exact
            path={[
              '/event-dashboard/:eventDashboardId',
              '/event-dashboard/:eventDashboardId/report',
              '/event-dashboard/:eventDashboardId/list',
              '/event-dashboard/:eventDashboardId/list\\::filterConfig',
            ]}
            component={EventsLayout}
          />

          <PrivateRoute
            actions={this.props.actions}
            exact
            path="/settings"
            component={SettingsView}
          />
          <PrivateRoute
            actions={this.props.actions}
            path="/settings/:settingsType"
            component={SettingsView}
          />

          <Redirect
            from="*"
            to={{
              pathname: '/404',
              state: { referrer: this.props.match.path },
            }}
          />
        </Switch>
        {isModal
          ? (
            <PrivateRoute
              actions={this.props.actions}
              exact
              fullModal
              isModal={isModal}
              previousPath={previousPath}
              path={[
                '/import/customers',
                '/import/customers/map',
                '/import/customers/review/:connectionRunId',
                '/import/teams',
                '/import/teams/map',
                '/import/teams/review/:connectionRunId',
                '/import/match-payments',
                '/import/match-payments/map',
                '/import/match-payments/review/:connectionRunId',
              ]}
              component={ImportsLayout}
            />
          ) : null}
        {/* {isModal
          ? <Route exact path="/import/customers"><FullModal isModal={isModal} /></Route>
          : null} */}
      </>
    );
  }
}
